import React, { useCallback, useReducer } from "react";
import { EditButton, useNotify, useRefresh, useShowController, useTranslate } from "react-admin";

import { getHttpClient } from "../infrastructure/dataProvider";
import { getApiBase } from "../utils/api";
import { ShowToolbar } from "./ShowToolbar";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import DoneIcon from "@mui/icons-material/Done";

const commonStyles = {
  toolbar: {
    textAlign: "right",
  },
  button: (theme: any) => ({
    marginLeft: theme.spacing(2),
  }),
  asDefaultButton: {
    color: "rgb(30, 136, 229)",
    "&:hover": {
      backgroundColor: "rgb(30, 136, 229, 0.12)",
    },
  },
};

type State = boolean;
type Action = {
  type: "fetched" | "fetching";
};
function reducer(state: State, action: Action) {
  switch (action.type) {
    case "fetching":
      return true;
    case "fetched":
      return false;
    default:
      throw new Error();
  }
}

const canEdit = (permissions: any) => {
  return ["admin", "staff"].includes(permissions);
};

export const WholesalerAddressShowActions = (props: { permissions: string }) => {
  const { resource, record } = useShowController();
  const { permissions } = props;

  if (!permissions || !record) {
    return null;
  }

  return (
    <ShowToolbar {...props} listPath="/wholesaler_addresses">
      <Box sx={commonStyles.toolbar}>
        {canEdit(permissions) && (
          <Box component="span" sx={commonStyles.button}>
            <AsDefaultButton record={record} />
          </Box>
        )}
        {canEdit(permissions) && (
          <Box component="span" sx={commonStyles.button}>
            <EditButton resource={resource} record={record} />
          </Box>
        )}
      </Box>
    </ShowToolbar>
  );
};

export const AsDefaultButton = (props: { record: any }) => {
  const { record } = props;

  const translate = useTranslate();
  const [isLoading, dispatch] = useReducer(reducer, false);
  const notify = useNotify();
  const refresh = useRefresh();

  const { id } = record;

  const onClickButton = useCallback(async () => {
    dispatch({ type: "fetching" });
    try {
      const httpClient = getHttpClient();
      const url = `${getApiBase()}/wholesalers/me/default_wholesaler_address`;
      const opts = {
        method: "PUT",
        body: JSON.stringify({ id }),
      };
      const res = await httpClient(url, opts);
      if (res.body.id === id) {
        notify("ra.notification.updated", { type: "info", messageArgs: { smart_count: 1 } });
        refresh();
      } else {
        notify("ra.message.error", { type: "error" });
      }
    } catch (e) {
      notify((e as any).message, { type: "error" });
    }
    dispatch({ type: "fetched" });
  }, [dispatch, id, notify, refresh]);

  return (
    <Button
      sx={commonStyles.asDefaultButton}
      size="small"
      startIcon={<DoneIcon />}
      disabled={isLoading || record.is_default}
      onClick={onClickButton}
    >
      {translate("ui.action.set_as_default")}
    </Button>
  );
};
